import { Trash2 } from "lucide-react";
import { useEffect, useId, useState, type FC, type ReactNode } from "react";

import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import {
  Field,
  FieldDescription,
  FieldError,
  FieldLabel
} from "@/components/ui/field";
import { Input } from "@/components/ui/input";
import { Spinner } from "@/components/ui/spinner";

type Props = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  description: ReactNode;
  /** The text the user has to type (case-insensitive). */
  expected: string;
  /** What `expected` is, for the prompt and placeholder, e.g. "last name". */
  expectedNoun?: string;
  /** Shown when `expected` is blank and the action can't be confirmed. */
  missingExpectedMessage?: ReactNode;
  confirmLabel?: string;
  isPending?: boolean;
  onConfirm: () => void;
};

/** Destructive action gated on retyping a known value. */
export const TypeToConfirmDialog: FC<Props> = ({
  open,
  onOpenChange,
  title,
  description,
  expected,
  expectedNoun = "last name",
  missingExpectedMessage,
  confirmLabel = "Delete",
  isPending = false,
  onConfirm
}) => {
  const id = useId();
  const [input, setInput] = useState("");
  const target = expected.trim();
  const matches =
    Boolean(target) && input.trim().toLowerCase() === target.toLowerCase();
  const showMismatch = Boolean(target) && input.length > 0 && !matches;
  const canConfirm = matches && !isPending;

  useEffect(() => {
    if (!open) setInput("");
  }, [open]);

  return (
    <AlertDialog
      open={open}
      onOpenChange={(next) => {
        if (!next && isPending) return;
        onOpenChange(next);
      }}
    >
      <AlertDialogContent className="sm:max-w-md">
        <AlertDialogHeader>
          <AlertDialogTitle>{title}</AlertDialogTitle>
          <AlertDialogDescription>{description}</AlertDialogDescription>
        </AlertDialogHeader>

        <Field data-invalid={showMismatch || undefined}>
          <FieldLabel htmlFor={id}>
            To confirm, type{" "}
            <span className="font-semibold">
              {target || `the ${expectedNoun}`}
            </span>{" "}
            below
          </FieldLabel>
          <Input
            id={id}
            autoFocus
            value={input}
            placeholder={expectedNoun.replace(/^\w/, (c) => c.toUpperCase())}
            disabled={!target || isPending}
            aria-invalid={showMismatch || undefined}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && canConfirm) {
                e.preventDefault();
                onConfirm();
              }
            }}
          />
          {!target ? (
            <FieldDescription>
              {missingExpectedMessage ??
                `The ${expectedNoun} is missing, so this can't be confirmed.`}
            </FieldDescription>
          ) : null}
          {showMismatch ? (
            <FieldError>The {expectedNoun} doesn't match.</FieldError>
          ) : null}
        </Field>

        <AlertDialogFooter>
          <AlertDialogCancel disabled={isPending}>Cancel</AlertDialogCancel>
          <Button
            variant="destructive"
            disabled={!canConfirm}
            onClick={onConfirm}
          >
            {isPending ? (
              <Spinner className="size-3.5" />
            ) : (
              <Trash2 className="size-3.5" />
            )}
            {isPending ? "Deleting…" : confirmLabel}
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};
